// src/api/workflowApi.js - API COMPLETA para flujo de 3 pasos
import apiClient from './apiClient';

const workflowApi = {
  // Iniciar proceso de inscripción
  iniciarInscripcion: (entidadId) => {
    console.log('API: Iniciando inscripción para entidad:', entidadId);
    return apiClient.post(`/inscripcionworkflow/iniciar/${entidadId}`);
  },

  // Obtener estado del workflow
  obtenerEstadoInscripcion: (entidadId) => {
    console.log('API: Obteniendo estado de inscripción para entidad:', entidadId);
    return apiClient.get(`/inscripcionworkflow/estado/${entidadId}`)
      .then(response => {
        console.log('API: Estado obtenido:', response.data);
        return response;
      })
      .catch(error => {
        console.error('API: Error obteniendo estado:', error);
        throw error;
      });
  },

  // Validar registro completo
  validarRegistroCompleto: (entidadId) => {
    console.log('API: Validando registro completo para entidad:', entidadId);
    return apiClient.post('/inscripcionworkflow/validar-registro', {
      entidadId,
      forzarValidacion: false
    });
  },

  // Obtener documentos requeridos
  obtenerDocumentosRequeridos: (workflowNegocioId) => {
    console.log('API: Obteniendo documentos requeridos para workflow:', workflowNegocioId);
    return apiClient.get(`/inscripcionworkflow/documentos-requeridos/${workflowNegocioId}`)
      .then(response => {
        console.log('API: Documentos obtenidos:', response.data);
        return response;
      })
      .catch(error => {
        console.error('API: Error obteniendo documentos:', error);
        throw error;
      });
  },

  // Obtener documentos por entidad (método alternativo)
  obtenerDocumentosPorEntidad: (entidadId) => {
    console.log('API: Obteniendo documentos por entidad:', entidadId);
    return apiClient.get(`/inscripcionworkflow/documentos-por-entidad/${entidadId}`);
  },

  // PASO 1: Subir documento (solo sube el archivo, queda en estado SUBIDO)
  subirDocumento: (documentoData) => {
    console.log('API: Subiendo documento (paso 1):', documentoData);
    
    // Validar datos requeridos para subir
    const camposRequeridos = ['workflowNegocioId', 'tipoDocumentoId', 'nombreArchivo', 'rutaArchivo'];
    for (const campo of camposRequeridos) {
      if (!documentoData[campo]) {
        console.error(`API: Campo requerido faltante: ${campo}`);
        return Promise.reject(new Error(`El campo ${campo} es requerido`));
      }
    }
    
    return apiClient.post('/inscripcionworkflow/subir-documento', {
      workflowNegocioId: documentoData.workflowNegocioId,
      tipoDocumentoId: documentoData.tipoDocumentoId,
      nombreArchivo: documentoData.nombreArchivo,
      rutaArchivo: documentoData.rutaArchivo,
      mimeType: documentoData.mimeType || 'application/pdf',
      tamanoBytes: documentoData.tamanoBytes || 0,
      usuarioSubida: documentoData.usuarioSubida || null,
      urlDescarga: documentoData.urlDescarga || null
    })
    .then(response => {
      console.log('API: Documento subido exitosamente (paso 1):', response.data);
      return response;
    })
    .catch(error => {
      console.error('API: Error subiendo documento:', error.response?.data || error.message);
      throw error;
    });
  },

  // PASO 2: Radicar documento ya subido (cambia de SUBIDO a RADICADO)
  radicarDocumento: (radicarData) => {
    console.log('API: Radicando documento (paso 2):', radicarData);
    
    // Para radicar solo necesitas el ID del documento requerido
    if (!radicarData.documentoRequeridoId) {
      return Promise.reject(new Error('El documentoRequeridoId es requerido para radicar'));
    }
    
    return apiClient.post('/inscripcionworkflow/radicar-documento', {
      documentoRequeridoId: radicarData.documentoRequeridoId,
      comentario: radicarData.comentario || null
    })
    .then(response => {
      console.log('API: Documento radicado exitosamente (paso 2):', response.data);
      return response;
    })
    .catch(error => {
      console.error('API: Error radicando documento:', error.response?.data || error.message);
      throw error;
    });
  },

  // PASO 3: Iniciar revisión de documento (Admin)
  iniciarRevisionDocumento: (documentoRequeridoId, usuarioRevisor = null) => {
    console.log('API: Iniciando revisión de documento (paso 3):', documentoRequeridoId);
    return apiClient.post('/inscripcionworkflow/iniciar-revision-documento', {
      documentoRequeridoId,
      usuarioRevisor,
      comentarioInicio: 'Iniciando revisión del documento'
    })
    .then(response => {
      console.log('API: Revisión iniciada exitosamente:', response.data);
      return response;
    })
    .catch(error => {
      console.error('API: Error iniciando revisión:', error);
      throw error;
    });
  },

  // PASO 3: Finalizar revisión de documento (Admin - Aprobar/Rechazar)
  finalizarRevisionDocumento: (revisionData) => {
    console.log('API: Finalizando revisión de documento (paso 3):', revisionData);
    return apiClient.post('/inscripcionworkflow/finalizar-revision-documento', revisionData)
      .then(response => {
        console.log('API: Revisión finalizada exitosamente:', response.data);
        return response;
      })
      .catch(error => {
        console.error('API: Error finalizando revisión:', error);
        throw error;
      });
  },

  // Obtener resumen de estados
  obtenerResumenEstados: (workflowNegocioId) => {
    console.log('API: Obteniendo resumen de estados para workflow:', workflowNegocioId);
    return apiClient.get(`/inscripcionworkflow/resumen-estados/${workflowNegocioId}`);
  },

  // Obtener historial del workflow
  obtenerHistorialWorkflow: (entidadId) => {
    console.log('API: Obteniendo historial de workflow para entidad:', entidadId);
    return apiClient.get(`/inscripcionworkflow/historial-completo/${entidadId}`)
      .then(response => {
        console.log('API: Historial de workflow obtenido:', response.data);
        return response;
      })
      .catch(error => {
        console.error('API: Error obteniendo historial de workflow:', error);
        throw error;
      });
  },

  // Obtener tipos de documentos requeridos (para configuración)
  obtenerTiposDocumentosRequeridos: (entidadId = null) => {
    const endpoint = entidadId 
      ? `/inscripcionworkflow/tipos-documentos/${entidadId}`
      : '/inscripcionworkflow/tipos-documentos';
    console.log('API: Obteniendo tipos de documentos requeridos');
    return apiClient.get(endpoint);
  },

  // Validar estado de documentos
  validarEstadoDocumentos: (workflowNegocioId) => {
    console.log('API: Validando estado de documentos para workflow:', workflowNegocioId);
    return apiClient.post('/inscripcionworkflow/validar-estado-documentos', {
      workflowNegocioId
    });
  },

  // Dashboard de administración
  obtenerDashboardAdmin: (filtros = {}) => {
    const params = new URLSearchParams();
    
    if (filtros.pagina) params.append('pagina', filtros.pagina);
    if (filtros.tamano) params.append('tamano', filtros.tamano);
    if (filtros.filtroEstado) params.append('filtroEstado', filtros.filtroEstado);
    if (filtros.filtroEntidad) params.append('filtroEntidad', filtros.filtroEntidad);
    if (filtros.fechaInicio) params.append('fechaInicio', filtros.fechaInicio);
    if (filtros.fechaFin) params.append('fechaFin', filtros.fechaFin);

    console.log('API: Obteniendo dashboard admin con filtros:', filtros);
    return apiClient.get(`/inscripcionworkflow/activos?${params.toString()}`);
  },

  // Obtener estadísticas generales
  obtenerEstadisticasGenerales: () => {
    console.log('API: Obteniendo estadísticas generales');
    return apiClient.get('/inscripcionworkflow/estadisticas-generales');
  },

  // Descargar documento
  descargarDocumento: (documentoId) => {
    console.log('API: Solicitando descarga de documento:', documentoId);
    return apiClient.get(`/inscripcionworkflow/descargar-documento/${documentoId}`, {
      responseType: 'blob' // Para descargas de archivos
    });
  },

  // Obtener documento por ID
  obtenerDocumento: (documentoId) => {
    console.log('API: Obteniendo documento por ID:', documentoId);
    return apiClient.get(`/inscripcionworkflow/documento/${documentoId}`);
  },

  // Actualizar documento (para reemplazar documentos rechazados)
  actualizarDocumento: (documentoId, documentoData) => {
    console.log('API: Actualizando documento:', documentoId, documentoData);
    return apiClient.put(`/inscripcionworkflow/actualizar-documento/${documentoId}`, documentoData);
  },

  // Eliminar documento
  eliminarDocumento: (documentoId) => {
    console.log('API: Eliminando documento:', documentoId);
    return apiClient.delete(`/inscripcionworkflow/eliminar-documento/${documentoId}`);
  },

  // Notificar cambio de estado (para hooks/callbacks)
  notificarCambioEstado: (workflowNegocioId, estadoAnterior, estadoNuevo) => {
    console.log('API: Notificando cambio de estado:', {
      workflowNegocioId, 
      estadoAnterior, 
      estadoNuevo
    });
    return apiClient.post('/inscripcionworkflow/notificar-cambio-estado', {
      workflowNegocioId,
      estadoAnterior,
      estadoNuevo,
      fechaCambio: new Date().toISOString()
    });
  },

  // NUEVO: Reemplazar documento subido (estado 8)
  reemplazarDocumento: (documentoData) => {
    console.log('API: Reemplazando documento (estado 8):', documentoData);
    
    // Validar datos requeridos
    const camposRequeridos = ['documentoRequeridoId', 'nombreArchivo', 'rutaArchivo'];
    for (const campo of camposRequeridos) {
      if (!documentoData[campo]) {
        console.error(`API: Campo requerido faltante: ${campo}`);
        return Promise.reject(new Error(`El campo ${campo} es requerido`));
      }
    }
    
    return apiClient.put('/inscripcionworkflow/reemplazar-documento', {
      documentoRequeridoId: documentoData.documentoRequeridoId,
      nombreArchivo: documentoData.nombreArchivo,
      rutaArchivo: documentoData.rutaArchivo,
      mimeType: documentoData.mimeType || 'application/pdf',
      tamanoBytes: documentoData.tamanoBytes || 0,
      urlDescarga: documentoData.urlDescarga || null,
      comentario: documentoData.comentario || `Documento reemplazado: ${documentoData.nombreArchivoAnterior || 'archivo anterior'} → ${documentoData.nombreArchivo}`
    })
    .then(response => {
      console.log('API: Documento reemplazado exitosamente:', response.data);
      return response;
    })
    .catch(error => {
      console.error('API: Error reemplazando documento:', error.response?.data || error.message);
      throw error;
    });
  },

  // Método para manejar errores de manera consistente
  manejarError: (error, contexto = 'API') => {
    console.error(`${contexto}: Error en la operación`, {
      mensaje: error.message,
      codigo: error.response?.status,
      datos: error.response?.data,
      config: error.config
    });
    
    // Determinar tipo de error y mensaje apropiado
    let mensajeError = 'Error desconocido en la operación';
    
    if (error.response) {
      // Error de respuesta del servidor
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 400:
          mensajeError = data?.mensaje || 'Datos inválidos enviados al servidor';
          break;
        case 401:
          mensajeError = 'No autorizado. Verifique sus credenciales';
          break;
        case 403:
          mensajeError = 'No tiene permisos para realizar esta operación';
          break;
        case 404:
          mensajeError = 'Recurso no encontrado';
          break;
        case 409:
          mensajeError = data?.mensaje || 'Conflicto en la operación';
          break;
        case 422:
          mensajeError = data?.mensaje || 'Error de validación';
          break;
        case 500:
          mensajeError = 'Error interno del servidor';
          break;
        default:
          mensajeError = data?.mensaje || `Error del servidor (${status})`;
      }
    } else if (error.request) {
      // Error de red
      mensajeError = 'Error de conexión. Verifique su conexión a internet';
    } else {
      // Error de configuración
      mensajeError = error.message || 'Error en la configuración de la solicitud';
    }
    
    return {
      esExitoso: false,
      mensaje: mensajeError,
      codigoError: error.response?.status || 'NETWORK_ERROR',
      detalles: error.response?.data || null
    };
  },

  // ✅ NUEVO: Obtener historial completo para informes
  obtenerHistorialCompleto: (entidadId) => {
    console.log('API: Obteniendo historial completo para entidad:', entidadId);
    return apiClient.get(`/inscripcionworkflow/historial-completo/${entidadId}`)
      .then(response => {
        console.log('API: Historial completo obtenido:', response.data);
        return response;
      })
      .catch(error => {
        console.error('API: Error obteniendo historial completo:', error);
        throw error;
      });
  },

  // ✅ NUEVO: Generar y descargar informe PDF
  generarInformePdf: (entidadId) => {
    console.log('API: Generando informe PDF para entidad:', entidadId);
    return apiClient.get(`/inscripcionworkflow/generar-informe-pdf/${entidadId}`, {
      responseType: 'blob' // Importante para recibir archivos
    })
    .then(response => {
      console.log('API: Informe PDF generado exitosamente');
      
      // Crear URL para descarga
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Crear enlace temporal para descarga
      const link = document.createElement('a');
      link.href = url;
      link.download = `Informe_Inscripcion_${entidadId}_${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar URL
      window.URL.revokeObjectURL(url);
      
      return response;
    })
    .catch(error => {
      console.error('API: Error generando informe PDF:', error);
      throw error;
    });
  },

  // ✅ NUEVO: Obtener estadísticas avanzadas
  obtenerEstadisticasAvanzadas: (workflowNegocioId) => {
    console.log('API: Obteniendo estadísticas avanzadas para workflow:', workflowNegocioId);
    return apiClient.get(`/inscripcionworkflow/estadisticas-avanzadas/${workflowNegocioId}`)
      .then(response => {
        console.log('API: Estadísticas avanzadas obtenidas:', response.data);
        return response;
      })
      .catch(error => {
        console.error('API: Error obteniendo estadísticas avanzadas:', error);
        throw error;
      });
  },

  // ✅ NUEVO: Obtener entidad por ID (para informes)
  obtenerEntidadPorId: (entidadId) => {
    console.log('API: Obteniendo información de entidad:', entidadId);
    return apiClient.get(`/entidades/${entidadId}`)
      .then(response => {
        console.log('API: Información de entidad obtenida:', response.data);
        return response;
      })
      .catch(error => {
        console.error('API: Error obteniendo información de entidad:', error);
        throw error;
      });
  },

  // ✅ NUEVO: Exportar datos para informe (JSON)
  exportarDatosInforme: (entidadId) => {
    console.log('API: Exportando datos para informe de entidad:', entidadId);
    return apiClient.get(`/inscripcionworkflow/exportar-datos/${entidadId}`)
      .then(response => {
        console.log('API: Datos exportados exitosamente');
        
        // Crear archivo JSON para descarga
        const jsonData = JSON.stringify(response.data, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `Datos_Inscripcion_${entidadId}_${new Date().getTime()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        window.URL.revokeObjectURL(url);
        
        return response;
      })
      .catch(error => {
        console.error('API: Error exportando datos:', error);
        throw error;
      });
  },

  // ✅ UTILIDAD: Validar disponibilidad de informe
  validarDisponibilidadInforme: (entidadId) => {
    console.log('API: Validando disponibilidad de informe para entidad:', entidadId);
    return apiClient.get(`/inscripcionworkflow/validar-informe/${entidadId}`)
      .then(response => {
        console.log('API: Validación de disponibilidad completada:', response.data);
        return response;
      })
      .catch(error => {
        console.error('API: Error validando disponibilidad de informe:', error);
        throw error;
      });
  }

};

export default workflowApi;